import { MarketingOptInStatusDTO } from '@hexcrawl/shared-utils';
import api from './api';

export interface GuestResponse {
  userId: string;
  sessionToken: string;
  isGuest: boolean;
}

export interface UserResponse {
  user: {
    id: string;
    email?: string;
    pendingEmail?: string;
    isGuest: boolean;
    isAdmin?: boolean;
    hasAgeVerification?: boolean;
    oauthAccounts?: Array<{ provider: string; providerUsername?: string; providerAvatar?: string }>;
    marketingOptIn?: MarketingOptInStatusDTO;
    showMarketingPrompt?: boolean;
    showAccountLinkPrompt?: boolean;
    sessionExpiresAt?: string;
  } | null;
}

export interface LinkEmailResponse {
  ok: boolean;
  pendingEmail?: string;
  message?: string;
}

export interface VerifyEmailResponse {
  ok: boolean;
  email: string;
}

export interface VerifyRecoveryResponse {
  sessionToken: string;
  userId: string;
}

export const authService = {
  /**
   * Create guest account
   */
  async createGuest(dobClaim: Date): Promise<GuestResponse> {
    const response = await api.post<GuestResponse>('/auth/guest', { dobClaim: dobClaim.toISOString() });
    return response.data;
  },

  /**
   * Store date-of-birth self-attestation for the current user
   */
  async setDobClaim(dobClaim: Date): Promise<void> {
    await api.post('/auth/set-dob', { dobClaim: dobClaim.toISOString() });
  },

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<UserResponse> {
    const response = await api.get<UserResponse>('/auth/me');
    return response.data;
  },

  /**
   * Logout
   */
  async logout(): Promise<void> {
    await api.post('/auth/logout');
  },

  /**
   * Send email recovery/passwordless login link
   */
  async sendRecoveryLink(email: string): Promise<{ ok: boolean; message?: string }> {
    const response = await api.post('/auth/recovery/send', { email });
    return response.data;
  },

  /**
   * Verify recovery token to establish a session
   */
  async verifyRecoveryToken(token: string): Promise<VerifyRecoveryResponse> {
    const response = await api.post<VerifyRecoveryResponse>('/auth/recovery/verify', { token });
    return response.data;
  },

  /**
   * Link email to current account
   */
  async linkEmail(email: string): Promise<LinkEmailResponse> {
    const response = await api.post<LinkEmailResponse>('/auth/link/email', { email });
    return response.data;
  },

  /**
   * Consume an email-verification token, promoting the pending address to the account
   */
  async verifyEmailToken(token: string): Promise<VerifyEmailResponse> {
    const response = await api.post<VerifyEmailResponse>('/auth/link/verify-email', { token });
    return response.data;
  },

  /**
   * Hold off the account-link prompt. A snooze rather than a permanent dismissal — the server
   * re-offers it after a fixed window.
   */
  async dismissAccountLinkPrompt(): Promise<{ ok: boolean }> {
    const response = await api.post('/auth/link/dismiss-prompt', {});
    return response.data;
  },

  /**
   * Prepare OAuth account linking by storing session in a temporary server cookie
   */
  async setLinkSession(sessionToken: string): Promise<{ ok: boolean }> {
    const response = await api.post('/auth/link/session', { sessionToken });
    return response.data;
  },

  /**
   * Get OAuth URL
   */
  getOAuthUrl(provider: 'discord' | 'google'): string {
    return `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/auth/${provider}`;
  },
};
